/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/no-runloop, ember/require-computed-property-dependencies, no-unused-vars, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { debounce, later } from '@ember/runloop';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import { effortLabelsFor } from '@linc-technologies/linced-common/utils/effort-labels';
import {
  COMPONENT_FIELD_MAP,
  TEXT_COMPONENT_TYPES,
  PRINCIPAL_COMMENT_TEXT_TYPES,
  manualEditProperties,
  replaceNameAndPronouns,
  saveSection,
  queueSectionSave,
} from 'linced-app/utils/reporting';
import ReportingWriteMixin from 'linced-app/mixins/reporting-write';
import ReportingBaseMixin from 'linced-app/mixins/reporting-base';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';

export default class WriteController extends Controller.extend(
  ReportingWriteMixin,
  ReportingBaseMixin,
) {
  // Declared so the param binds reliably rather than relying on the route's
  // queryParams hash alone. Only periodIndex is listed — `mode` keeps its
  // existing route-level mechanism so its URL behaviour is unchanged.
  queryParams = ['periodIndex', 'periodEndDate', 'periodYear'];
  @tracked periodIndex = null;
  // The period's end date, which addresses it across template versions where
  // an index cannot. Set by the group cards; absent for an orphaned period.
  @tracked periodEndDate = null;
  // The academic year the period belongs to. Set by historic cards, which can
  // sit in a past year; absent means the current one.
  @tracked periodYear = null;

  @service
  personOptions;

  @service
  router;

  @service
  store;

  @service
  studentReportActions;

  @computed('parentController.editorOptions')
  get editorOptions() {
    return this.parentController.get('editorOptions');
  }

  @computed('parentController.progressChartColors')
  get progressChartColors() {
    return this.parentController.get('progressChartColors');
  }

  // Group-specific write state
  @tracked
  activeTextComponentTypes = null;

  @tracked
  checkedDraftStudents = null;

  @tracked
  isBulkSetting = false;

  @tracked
  bulkApplyDone = false;

  @tracked
  pendingBulkJudgement = null;

  @tracked
  pendingBulkEffort = null;

  // Evidence sidepeek state
  @tracked
  evidenceSidepeekOpen = false;

  @tracked
  evidenceSidepeekStudent = null;

  // Reuse narrative state
  @tracked
  reuseNarrativeStudent = null;

  @tracked
  reuseNarrativeComponentType = null;

  @tracked
  reuseNarrativeOpen = false;

  @tracked
  reuseNarrativeTargets = null;

  @tracked
  reuseNarrativeOtherStudents = null;

  @tracked
  reuseNarrativeConfirmCount = null;

  @computed(
    'reuseNarrativeTargets.[]',
    'reuseNarrativeOtherStudents.[]',
    'studentReportSectionMap',
    'activeSection.id',
  )
  get someButNotAllReuseTargetsChecked() {
    let sectionId = this.get('activeSection.id');
    let sectionMap = this.studentReportSectionMap;
    let targets = this.reuseNarrativeTargets;
    if (!targets || !targets.length) return false;
    let incomplete = (this.reuseNarrativeOtherStudents || []).filter(
      (student) => {
        let section =
          sectionMap &&
          sectionMap[student.get('id')] &&
          sectionMap[student.get('id')][sectionId];
        return !section || !section.get('complete');
      },
    );
    let checkedCount = incomplete.filter((s) => targets.includes(s)).length;
    return checkedCount > 0 && checkedCount < incomplete.length;
  }

  @computed(
    'reuseNarrativeTargets.[]',
    'reuseNarrativeOtherStudents.[]',
    'studentReportSectionMap',
    'activeSection.id',
  )
  get allReuseTargetsChecked() {
    let sectionId = this.get('activeSection.id');
    let sectionMap = this.studentReportSectionMap;
    let targets = this.reuseNarrativeTargets;
    if (!targets || !targets.length) return false;
    let incomplete = (this.reuseNarrativeOtherStudents || []).filter(
      (student) => {
        let section =
          sectionMap &&
          sectionMap[student.get('id')] &&
          sectionMap[student.get('id')][sectionId];
        return !section || !section.get('complete');
      },
    );
    if (!incomplete.length) return false;
    return incomplete.every((s) => targets.includes(s));
  }

  // Filter state
  @tracked
  selectedFilterOption = null;

  _allStudentsOption = Object.freeze({ label: 'All students', value: 'all' });

  @tracked
  _writeViewStudents;

  @tracked
  reuseNarrativeComponentLabel;

  @tracked
  addGoalsItems;

  @tracked
  addGoalsSelected;

  @tracked
  addGoalsStudent;

  @tracked
  addGoalsSectionLabel;

  @tracked
  addGoalsSubject;

  @tracked
  addGoalsCategory;

  @action
  setAddGoalsCategory(value) {
    this.addGoalsCategory = value;
  }

  @tracked
  addGoalsOpen;

  @tracked
  activeReport;

  @tracked
  activeReportContentSections;

  @tracked
  activeSection;

  @tracked
  writeReportsOpen;

  @tracked
  studentReportMap;

  @tracked
  otjProgressions;

  @tracked
  rubricDialogStudent;

  @tracked
  showRubricDialog;

  init() {
    super.init(...arguments);
    this.selectedFilterOption = this._allStudentsOption;
  }

  get isLeadershipCommentMode() {
    return this.activeTextComponentTypes === PRINCIPAL_COMMENT_TEXT_TYPES;
  }

  // todo confirm this works
  @computed('_writeViewStudents.[]', 'studentReportMap')
  get draftStudentsForActiveReport() {
    let students = this._writeViewStudents;
    let reportMap = this.studentReportMap;
    if (!students || !reportMap) {
      return [];
    }
    return students.filter((student) => !!reportMap[student.get('id')]);
  }

  @computed(
    'checkedDraftStudents.[]',
    'draftStudentsForActiveReport.[]',
    'studentReportSectionMap',
    'activeSection.id',
  )
  get allDraftStudentsChecked() {
    let all = this._incompleteDraftStudents();
    let checked = this.checkedDraftStudents;
    if (!checked || !all.length) return false;
    return all.every((s) => checked.includes(s));
  }

  @computed(
    'checkedDraftStudents.[]',
    'draftStudentsForActiveReport.[]',
    'allDraftStudentsChecked',
  )
  get someButNotAllDraftStudentsChecked() {
    let checked = this.checkedDraftStudents;
    if (!checked || !checked.length) return false;
    return !this.allDraftStudentsChecked;
  }

  @computed(
    'draftStudentsForActiveReport.[]',
    'studentReportSectionMap',
    'activeSection.id',
    'activeSectionSubject.possibleGrades.[]',
  )
  get studentFilterCountMap() {
    let students = this.draftStudentsForActiveReport;
    let sectionMap = this.studentReportSectionMap;
    let sectionId = this.get('activeSection.id');
    let levels = this.get('activeSectionSubject.possibleGrades') || [];
    let map = { all: students.length };
    if (!sectionMap || !sectionId) {
      map.missing = 0;
      return map;
    }
    map.missing = students.filter((student) => {
      let s = sectionMap[student.get('id')];
      if (!s) return true;
      let section = s[sectionId];
      return !section || !section.get('judgement');
    }).length;
    levels.forEach((level) => {
      map['judgement:' + level.value] = students.filter((student) => {
        let s = sectionMap[student.get('id')];
        if (!s) return false;
        let section = s[sectionId];
        return (
          section && parseInt(section.get('judgement'), 10) === level.value
        );
      }).length;
    });
    return map;
  }

  @computed(
    'draftStudentsForActiveReport.[]',
    'studentReportSectionMap',
    'activeSection.id',
    'activeSectionSubject.possibleGrades.[]',
  )
  get selectFilterOptions() {
    let options = [
      this._allStudentsOption,
      { label: 'Missing data', value: 'missing' },
    ];
    let levels = this.get('activeSectionSubject.possibleGrades');
    let students = this.draftStudentsForActiveReport;
    let sectionMap = this.studentReportSectionMap;
    let sectionId = this.get('activeSection.id');
    if (!levels || !students || !sectionMap || !sectionId) {
      return options;
    }
    let levelsInUse = new Set();
    students.forEach((student) => {
      let studentSections = sectionMap[student.get('id')];
      if (!studentSections) {
        return;
      }
      let section = studentSections[sectionId];
      if (section && section.get('judgement')) {
        levelsInUse.add(parseInt(section.get('judgement'), 10));
      }
    });
    levels.forEach((level) => {
      if (levelsInUse.has(level.value)) {
        options.push({
          label: 'At ' + level.string,
          value: 'judgement:' + level.value,
        });
      }
    });
    return options;
  }

  @computed('activeSectionSubject.possibleGrades.[]')
  get toolbarJudgementLevels() {
    return this.get('activeSectionSubject.possibleGrades') || [];
  }

  @computed('activeSectionSubject.{possibleGrades.[],school.jurisdiction}')
  get toolbarEffortLevels() {
    let possibleGrades = this.get('activeSectionSubject.possibleGrades') || [];
    let jurisdiction = this.get('activeSectionSubject.school.jurisdiction');
    return effortLabelsFor(jurisdiction).map((entry) => {
      let grade = possibleGrades.find((g) => g.value === entry.value);
      return {
        value: entry.value,
        string: entry.label,
        colour: grade ? grade.colour || '#26a69a' : '#26a69a',
      };
    });
  }

  @computed('activeSectionProgressComponents.[]')
  get hasToolbarEffort() {
    return (this.activeSectionProgressComponents || []).some(
      (c) =>
        c.type === 'effort' ||
        (c.type === 'strand-visual' && c.visualType === 'table-effort'),
    );
  }

  @computed('activeSectionProgressComponents.[]')
  get narrativeStepNumber() {
    let components = this.activeSectionProgressComponents || [];
    if (!components.length) return 1;
    let hasEffort = components.some((c) => c.type === 'effort');
    return hasEffort ? 3 : 2;
  }

  @computed('checkedDraftStudents.[]')
  get hasCheckedStudents() {
    return this.checkedDraftStudents && this.checkedDraftStudents.length > 0;
  }

  @computed('activeReportContentSections.[]', 'sectionCompletionMap')
  get sectionSelectOptions() {
    let sections = this.activeReportContentSections || [];
    let map = this.sectionCompletionMap || {};
    return sections.map((section) => ({
      value: section.get('id'),
      label: section.get('label'),
      sublabel: `${map[section.get('id')] || 0}% completed`,
    }));
  }

  @computed(
    'activeReportContentSections.[]',
    'draftStudentsForActiveReport.[]',
    'studentReportSectionMap',
  )
  get sectionCompletionMap() {
    let sections = this.activeReportContentSections;
    let students = this.draftStudentsForActiveReport;
    let sectionMap = this.studentReportSectionMap;
    if (!sections || !students || !sectionMap) return {};
    let total = students.length;
    let result = {};
    sections.forEach((section) => {
      if (!total) {
        result[section.get('id')] = 0;
        return;
      }
      let completed = students.filter((student) => {
        let studentSections = sectionMap[student.get('id')];
        let s = studentSections && studentSections[section.get('id')];
        return s && s.get('complete');
      }).length;
      result[section.get('id')] = Math.round((completed / total) * 100);
    });
    return result;
  }

  @computed('sectionCompletionMap')
  get sectionStateMap() {
    let map = this.sectionCompletionMap || {};
    let result = {};
    Object.keys(map).forEach((sectionId) => {
      result[sectionId] = { complete: map[sectionId] === 100 };
    });
    return result;
  }

  @computed('sectionCompletionMap', 'activeSection.id')
  get activeSectionFullyComplete() {
    let map = this.sectionCompletionMap || {};
    let id = this.get('activeSection.id');
    return id ? map[id] === 100 : false;
  }

  @computed('activeReportContentSections.[]', 'activeSection.id')
  get nextSection() {
    let sections = this.activeReportContentSections || [];
    let currentId = this.get('activeSection.id');
    let idx = sections.findIndex((s) => s.get('id') === currentId);
    return idx >= 0 ? sections[idx + 1] || null : null;
  }

  _isStudentComplete(student) {
    let sectionMap = this.studentReportSectionMap;
    let sectionId = this.get('activeSection.id');
    if (!sectionMap || !sectionId) return false;
    let studentSections = sectionMap[student.get('id')];
    if (!studentSections) return false;
    let section = studentSections[sectionId];
    return !!(section && section.get('complete'));
  }

  _incompleteDraftStudents() {
    return this.draftStudentsForActiveReport.filter(
      (s) => !this._isStudentComplete(s),
    );
  }

  _filteredStudents() {
    let students = this._incompleteDraftStudents();
    let option = this.selectedFilterOption;
    let mode = option ? option.value : 'all';
    if (mode === 'all') {
      return students.slice();
    }
    let sectionMap = this.studentReportSectionMap;
    let sectionId = this.get('activeSection.id');
    if (mode === 'missing') {
      return students.filter((student) => {
        let studentSections = sectionMap && sectionMap[student.get('id')];
        if (!studentSections) {
          return true;
        }
        let section = studentSections[sectionId];
        return !section || !section.get('judgement');
      });
    }
    if (mode.startsWith('judgement:')) {
      let targetValue = parseInt(mode.split(':')[1], 10);
      return students.filter((student) => {
        let studentSections = sectionMap && sectionMap[student.get('id')];
        if (!studentSections) {
          return false;
        }
        let section = studentSections[sectionId];
        return (
          section && parseInt(section.get('judgement'), 10) === targetValue
        );
      });
    }
    return students;
  }

  initWriteView(
    report,
    contentSections,
    group,
    isLeadership,
    periodIndex,
    periodEndDate,
    periodYear,
  ) {
    let groupCtrl = this.groupController;

    if (!group) {
      group = groupCtrl.get('selectedGroup');
    }

    let textTypes = isLeadership
      ? PRINCIPAL_COMMENT_TEXT_TYPES
      : TEXT_COMPONENT_TYPES;

    this.activeReport = report;
    this.activeReportContentSections = contentSections;
    this.activeSection = contentSections[0];
    this.activeTextComponentTypes = textTypes;
    this.checkedDraftStudents = [];
    this.pendingBulkJudgement = null;
    this.pendingBulkEffort = null;
    this.bulkApplyDone = false;
    this.writeReportsOpen = true;

    let reportId = report.get('id');
    let queryParams = {
      reportTemplateId: reportId,
      schoolId: this.session.activeSchool,
      groupId: group ? group.get('id') : null,
      // The card's own year, not a clock read. A historic card can sit in a
      // past academic year, and filtering to the current one asks for the
      // right period in the wrong year and comes back empty.
      year:
        typeof periodYear === 'number'
          ? periodYear
          : this.dates.currentAcademicYear,
      includeSections: true,
    };
    // periodEndDate reaches the period across every version of the template;
    // an index cannot, since a version that inserts or removes a period
    // renumbers the rest. The index is the fallback for an orphaned period,
    // which has no date left to resolve.
    //
    // Either way the period must be narrowed: without it a multi-period
    // template mixes every period into one write view. filterByPeriodIndex is
    // required for index 0, which is otherwise indistinguishable from "unset".
    if (periodEndDate) {
      queryParams.periodEndDate = periodEndDate;
    } else if (typeof periodIndex === 'number') {
      queryParams.periodIndex = periodIndex;
      queryParams.filterByPeriodIndex = true;
    }
    if (isLeadership) {
      queryParams.isAwaitingLeadershipComment = true;
    } else {
      queryParams.state = StudentReportState.DRAFT;
    }

    this.store
      .query('reports/student-report', queryParams)
      .then((relevantReports) => {
        let sectionMap = {};
        let reportMap = {};

        let allSections = this.store.peekAll('reports/student-report-section');
        relevantReports.forEach((studentReport) => {
          let studentId = studentReport.belongsTo('studentId').id();
          reportMap[studentId] = studentReport;

          let reportSections = allSections.filter(
            (s) =>
              s.belongsTo('studentReportId').id() === studentReport.get('id'),
          );

          sectionMap[studentId] = {};
          reportSections.forEach((section) => {
            let templateSectionId = section
              .belongsTo('reportTemplateSectionId')
              .id();
            sectionMap[studentId][templateSectionId] = section;
          });
        });

        let draftStudents = sortBy(
          relevantReports
            .map((r) =>
              this.store.peekRecord('person', r.belongsTo('studentId').id()),
            )
            .filter(Boolean),
          'fullName',
        );

        this._writeViewStudents = draftStudents;
        this.set('studentReportSectionMap', sectionMap);
        this.studentReportMap = reportMap;

        this._registerBeforeUnloadSave();
        this._loadStudentPosts(draftStudents);
        this._loadTriangulationData(draftStudents);
      })
      .catch(() => {
        this.toaster.error('Failed to load student reports');
      });
  }

  cleanupWriteView() {
    this._deregisterBeforeUnloadSave();
    this.setProperties({
      writeReportsOpen: false,
      activeReport: null,
      activeSection: null,
      activeReportContentSections: null,
      activeTextComponentTypes: null,
      checkedDraftStudents: null,
      studentReportSectionMap: null,
      studentReportMap: null,
      _writeViewStudents: null,
      postCountMap: null,
      studentPostsMap: null,
      evidenceSidepeekOpen: false,
      evidenceSidepeekStudent: null,
      showRubricDialog: false,
      rubricDialogStudent: null,
      reuseNarrativeOpen: false,
      reuseNarrativeStudent: null,
      reuseNarrativeComponentType: null,
      reuseNarrativeComponentLabel: null,
      reuseNarrativeTargets: null,
      reuseNarrativeOtherStudents: null,
      reuseNarrativeConfirmCount: null,
      addGoalsOpen: false,
      addGoalsStudent: null,
      addGoalsSectionLabel: null,
      addGoalsSubject: null,
      addGoalsCategory: null,
      addGoalsItems: null,
      addGoalsSelected: null,
    });
    this._clearTriangulationData();
  }

  @action
  async exitWriteReports() {
    // Await pending saves before leaving; the group hub refetches counts on
    // arrival and would otherwise read pre-edit state.
    await this.flushPendingSectionSaves();
    if (this.isDestroyed || this.isDestroying) return;
    this.cleanupWriteView();
    this.router.transitionTo('reporting.group');
  }

  @action
  toggleDraftStudent(student) {
    if (this._isStudentComplete(student)) return;
    if (this.checkedDraftStudents.includes(student)) {
      this.checkedDraftStudents = this.checkedDraftStudents.filter(
        (s) => s !== student,
      );
    } else {
      this.checkedDraftStudents = [...this.checkedDraftStudents, student];
    }
  }

  @action
  toggleAllDraftStudents() {
    if (
      this.allDraftStudentsChecked ||
      this.someButNotAllDraftStudentsChecked
    ) {
      this.checkedDraftStudents = [];
    } else {
      this.checkedDraftStudents = this._filteredStudents();
    }
  }

  @action
  selectStudentsByFilter(value) {
    let option = this.selectFilterOptions.find((o) => o.value === value);
    this.selectedFilterOption = option || this._allStudentsOption;
    this.checkedDraftStudents = this._filteredStudents();
  }

  @action
  setPendingJudgement(value) {
    this.pendingBulkJudgement =
      this.pendingBulkJudgement === value ? null : value;
  }

  @action
  setPendingEffort(value) {
    this.pendingBulkEffort = this.pendingBulkEffort === value ? null : value;
  }

  // `{{action (mut ...) value="target.value"}}` unwrapped the select value for
  // us; `{{on}}` hands over the event, so unwrap it here before setting.
  @action
  setPendingBulkJudgement(event) {
    this.pendingBulkJudgement = event.target.value;
  }

  @action
  setPendingBulkEffort(event) {
    this.pendingBulkEffort = event.target.value;
  }

  @action
  applyBulkSettings() {
    let j = this.pendingBulkJudgement;
    let e = this.pendingBulkEffort;
    if (this.isBulkSetting || (!j && !e)) return;
    let students = this.checkedDraftStudents;
    if (!students || !students.length) return;

    this.isBulkSetting = true;
    this.pendingBulkJudgement = null;
    this.pendingBulkEffort = null;

    if (j)
      students.forEach((student) =>
        this._updateWriteJudgement(student, null, j),
      );
    if (e)
      students.forEach((student) => this._updateWriteEffort(student, null, e));
    this.notifyPropertyChange('studentReportSectionMap');

    later(
      this,
      () => {
        if (this.isDestroyed || this.isDestroying) return;
        this.isBulkSetting = false;
        this.bulkApplyDone = true;
        later(
          this,
          () => {
            if (this.isDestroyed || this.isDestroying) return;
            this.bulkApplyDone = false;
          },
          1500,
        );
      },
      1500,
    );
  }

  @action
  bulkSetJudgement(value) {
    let students = this.checkedDraftStudents;
    if (!students || !students.length || this.isBulkSetting) {
      return;
    }
    this.isBulkSetting = true;
    students.forEach((student) => {
      this._updateWriteJudgement(student, null, value);
    });
    this.notifyPropertyChange('studentReportSectionMap');
    // Section saves are debounced by 1000ms inside _updateWriteJudgement;
    // clear the flag once saves have had time to flush.
    later(
      this,
      () => {
        if (this.isDestroyed || this.isDestroying) return;
        this.isBulkSetting = false;
      },
      1500,
    );
  }

  @action
  bulkSetEffort(value) {
    let students = this.checkedDraftStudents;
    if (!students || !students.length || this.isBulkSetting) {
      return;
    }
    this.isBulkSetting = true;
    students.forEach((student) => {
      this._updateWriteEffort(student, null, value);
    });
    this.notifyPropertyChange('studentReportSectionMap');
    later(
      this,
      () => {
        if (this.isDestroyed || this.isDestroying) return;
        this.isBulkSetting = false;
      },
      1500,
    );
  }

  @action
  openReuseNarrative(student, componentType, componentLabel) {
    let others = this.draftStudentsForActiveReport.filter(
      (s) => s.get('id') !== student.get('id'),
    );
    this.reuseNarrativeStudent = student;
    this.reuseNarrativeComponentType = componentType;
    this.reuseNarrativeComponentLabel = componentLabel;
    this.reuseNarrativeTargets = [];
    this.reuseNarrativeOtherStudents = others;
    this.reuseNarrativeOpen = true;
  }

  @action
  closeReuseNarrative() {
    this.reuseNarrativeOpen = false;
    this.reuseNarrativeStudent = null;
    this.reuseNarrativeComponentLabel = null;
    this.reuseNarrativeTargets = null;
  }

  @action
  toggleReuseTarget(student) {
    let targets = this.reuseNarrativeTargets;
    if (targets.includes(student)) {
      this.reuseNarrativeTargets = targets.filter((s) => s !== student);
    } else {
      this.reuseNarrativeTargets = [...targets, student];
    }
  }

  @action
  selectAllReuseTargets() {
    if (this.allReuseTargetsChecked) {
      this.reuseNarrativeTargets = [];
    } else {
      let sectionId = this.get('activeSection.id');
      let sectionMap = this.studentReportSectionMap;
      let incomplete = (this.reuseNarrativeOtherStudents || []).filter(
        (student) => {
          let section =
            sectionMap &&
            sectionMap[student.get('id')] &&
            sectionMap[student.get('id')][sectionId];
          return !section || !section.get('complete');
        },
      );
      this.reuseNarrativeTargets = incomplete;
    }
  }

  @action
  confirmReuseNarrative() {
    let source = this.reuseNarrativeStudent;
    let componentType = this.reuseNarrativeComponentType;
    let field = COMPONENT_FIELD_MAP[componentType];
    if (!field || !source) {
      return;
    }
    let sectionId = this.get('activeSection.id');
    let sourceSection = this.getStudentReportSection(
      source.get('id'),
      sectionId,
    );
    if (!sourceSection) {
      return;
    }
    let sourceHtml = sourceSection.get(field) || '';
    let targets = this.reuseNarrativeTargets;
    targets.forEach((target) => {
      let targetSection = this.getStudentReportSection(
        target.get('id'),
        sectionId,
      );
      if (!targetSection) {
        return;
      }
      let adapted = replaceNameAndPronouns(
        sourceHtml,
        source,
        target,
        this.activeReport,
      );
      // Reused text counts as a manual edit, so sliders won't replace it
      targetSection.setProperties(manualEditProperties(field, adapted));
      debounce(targetSection, saveSection, targetSection, this.toaster, 1000);
    });
    this.notifyPropertyChange('studentReportSectionMap');
    this.reuseNarrativeConfirmCount = targets.length;
    this.reuseNarrativeOpen = false;
    this.reuseNarrativeStudent = null;
    this.reuseNarrativeTargets = null;
  }

  @action
  dismissReuseConfirmation() {
    this.reuseNarrativeConfirmCount = null;
  }

  @action
  openAddGoals(student, sectionLabel) {
    this.addGoalsStudent = student;
    this.addGoalsSectionLabel = sectionLabel || null;
    this.addGoalsSubject = null;
    this.addGoalsCategory = null;
    this.addGoalsItems = null;
    this.addGoalsSelected = [];
    this.addGoalsOpen = true;
  }

  @action
  closeAddGoals() {
    this.addGoalsOpen = false;
    this.addGoalsStudent = null;
    this.addGoalsSectionLabel = null;
    this.addGoalsSubject = null;
    this.addGoalsCategory = null;
    this.addGoalsItems = null;
    this.addGoalsSelected = null;
  }

  @action
  async selectAddGoalsSubject(subject) {
    this.addGoalsSubject = subject;
    this.addGoalsCategory = null;
    this.addGoalsSelected = [];
    try {
      let goals = await this.store.query('goals/goal', {
        subjectId: subject.get('id'),
      });
      this.addGoalsItems = goals;
    } catch (e) {
      this.addGoalsItems = [];
    }
  }

  @action
  filterAddGoalsByCategory(category) {
    if (
      this.addGoalsCategory &&
      this.addGoalsCategory.get('id') === category.get('id')
    ) {
      this.addGoalsCategory = null;
    } else {
      this.addGoalsCategory = category;
    }
  }

  @action
  toggleAddGoal(goal) {
    let selected = this.addGoalsSelected;
    if (selected.includes(goal)) {
      this.addGoalsSelected = selected.filter((g) => g !== goal);
    } else {
      this.addGoalsSelected = [...selected, goal];
    }
  }

  @action
  confirmAddGoals() {
    let student = this.addGoalsStudent;
    if (!student) return;
    let sectionId = this.get('activeSection.id');
    let section = this.getStudentReportSection(student.get('id'), sectionId);
    let changed = this.studentReportActions.confirmAddGoals(
      section,
      this.addGoalsSelected,
    );
    if (changed) {
      this.notifyPropertyChange('studentReportSectionMap');
    }
    this.addGoalsOpen = false;
    this.addGoalsStudent = null;
    this.addGoalsSectionLabel = null;
    this.addGoalsSubject = null;
    this.addGoalsCategory = null;
    this.addGoalsItems = null;
    this.addGoalsSelected = null;
  }

  @action
  async markSectionComplete(student) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) {
      return;
    }
    section.set('complete', true);
    await queueSectionSave(section);
    this.notifyPropertyChange('studentReportSectionMap');

    let studentSections = this.studentReportSectionMap[student.get('id')];
    if (!studentSections) {
      return;
    }
    let sectionIds = this.activeReportContentSections.map((s) => s.get('id'));
    let allComplete = sectionIds.every((id) => {
      let s = studentSections[id];
      return s && s.get('complete');
    });

    if (allComplete && !this.isLeadershipCommentMode) {
      let studentReport = this.studentReportMap[student.get('id')];
      if (
        studentReport &&
        studentReport.get('state') === StudentReportState.DRAFT
      ) {
        try {
          await this.studentReportActions.submit(studentReport);
        } catch (err) {
          this.toaster.error('Failed to mark section complete');
        }
      }
    }
  }

  @action
  async editSection(student) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) {
      return;
    }
    section.set('complete', false);
    await queueSectionSave(section);
    this.notifyPropertyChange('studentReportSectionMap');

    // In leadership mode the principal is editing their comment while the
    // report stays in READY_TO_REVIEW — don't reopen it.
    if (this.isLeadershipCommentMode) return;

    let studentReport = this.studentReportMap[student.get('id')];
    if (
      studentReport &&
      studentReport.get('state') === StudentReportState.READY_TO_REVIEW
    ) {
      try {
        await this.studentReportActions.reopen(studentReport);
      } catch (e) {
        this.toaster.error('Failed to edit section');
      }
    }
  }

  @action
  updateSectionField(student, componentType, value) {
    this._updateWriteSectionField(student, componentType, value);
  }

  @action
  autoPopulateSection(student, populateData) {
    this._autoPopulateWriteSection(student, populateData);
  }

  @action
  regenerateSectionField(student, componentType) {
    this._regenerateWriteSectionField(student, componentType);
  }

  @action
  selectSectionByValue(id) {
    let section = (this.activeReportContentSections || []).find(
      (s) => s.get('id') === id,
    );
    if (section) {
      this.selectSection(section);
    }
  }

  @action
  selectSectionById(e) {
    let id = e.target.value;
    let section = (this.activeReportContentSections || []).find(
      (s) => s.get('id') === id,
    );
    if (section) {
      this.selectSection(section);
    }
  }

  @action
  selectSection(section) {
    this.activeSection = section;
    this.pendingBulkJudgement = null;
    this.pendingBulkEffort = null;
    this.bulkApplyDone = false;

    if (this.writeReportsOpen) {
      // Use _writeViewStudents directly — draftInfo.students was always empty
      // on first load (only _buildEntryFromReports populated it, and only
      // after a submit/reopen), so posts and triangulation data were silently
      // skipped until a state transition occurred.
      this._loadStudentPosts(this._writeViewStudents);
      this._loadTriangulationData(this._writeViewStudents);
    }
  }

  @action
  otjChartData(records) {
    return this._otjChartData(records);
  }

  @action
  async updateOtjRecords() {
    let subject = this.otjSubject;
    if (!subject) return;
    let subjectId = subject.get('id');
    let progressions = await this.store.query('milestones/progression', {
      goalSubjectId: subjectId,
      type: ProgressionType.PROGRESSION,
      latest: true,
    });

    // Use _writeViewStudents directly — same latent bug as in selectSection:
    // draftInfo.students was only populated after a state transition, so the
    // progressionMap would be empty on first load.
    let students = this._writeViewStudents || [];
    let progressionMap = {};
    students.forEach((s) => {
      progressionMap[s.get('id')] = [];
    });
    progressions.forEach((p) => {
      let personId =
        p.person_id ||
        (p.belongsTo &&
          p.belongsTo('personId') &&
          p.belongsTo('personId').id());
      if (personId && progressionMap[personId]) {
        progressionMap[personId].push(p);
      }
    });

    this.otjProgressions = progressionMap;
  }

  @action
  onOtjJudgementChange(student, judgementValue) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) {
      return;
    }
    section.set('judgement', String(judgementValue));
    if (section.get('complete')) {
      section.set('complete', false);
    }
    saveSection(section, this.toaster);
  }

  @action
  openEvidenceSidepeek(student) {
    this.evidenceSidepeekStudent = student;
    this.evidenceSidepeekOpen = true;
  }

  @action
  closeEvidenceSidepeek() {
    this.evidenceSidepeekOpen = false;
    this.evidenceSidepeekStudent = null;
  }

  @action
  addPostToNarrative(student, post) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) return;

    let narrativeComponent = (this.activeSectionTextComponents || []).find(
      (c) => COMPONENT_FIELD_MAP[c.type] === 'narrative',
    );
    if (!narrativeComponent) return;
    let field = COMPONENT_FIELD_MAP[narrativeComponent.type];
    if (!field) return;

    let postText = (post.get('content') || post.get('subject') || '').trim();
    if (!postText) return;

    let existing = (section.get(field) || '').trim();
    // Adding a post counts as a manual edit, so sliders won't replace it
    section.setProperties(
      manualEditProperties(
        field,
        existing ? `${existing}\n${postText}` : postText,
      ),
    );
    if (section.get('complete')) {
      section.set('complete', false);
    }
    this.notifyPropertyChange('studentReportSectionMap');
    debounce(section, saveSection, section, this.toaster, 1000);
  }

  @action
  updateJudgement(student, elementId, value) {
    this._updateWriteJudgement(student, elementId, value);
  }

  @action
  updateEffort(student, elementId, value) {
    this._updateWriteEffort(student, elementId, value);
  }

  @action
  openRubricDialog(student) {
    this.rubricDialogStudent = student;
    this.showRubricDialog = true;
  }

  @action
  closeRubricDialog() {
    this.showRubricDialog = false;
    this.rubricDialogStudent = null;
  }
}
